import {Component, EventEmitter, inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl} from "@angular/forms";
import {IonContent, IonInfiniteScroll, ModalController} from "@ionic/angular";
import {debounceTime} from "rxjs/operators";
import {ApiResponse} from "../../services/utils";
import {lastValueFrom} from "rxjs";
import {UserService} from "../../services/user/user.service";
import {AuthService} from "../../services/auth/auth.service";
import {ActionPointService} from "../../services/action-points/action-point.service";
import {PostModel} from "../../models/post.model";
import {GlobalService} from "../../services/global/global.service";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: 'app-ap-assign-moment-modal',
    templateUrl: './ap-assign-moment-modal.component.html',
    styleUrls: ['./ap-assign-moment-modal.component.scss'],
    standalone: false
})
export class ApAssignMomentModalComponent implements OnInit {
  @Input() clickable: boolean = false;
  @Input() momentCard: boolean = false;
  @Input('type') type: 'user' | 'organization' = 'user';
  @Input('id') id: string = '';
  @Output() actionPointSelected: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild(IonContent) content!: IonContent;
  @ViewChild(IonInfiniteScroll) infiniteScroll!: IonInfiniteScroll;

  public selectedActionPoint: number | null = null;
  private searchStarted: boolean = false;
  public loading: boolean = false;
  public actionPointListIsLoaded: boolean = false;
  public hasMoreData: boolean = true;

  public pageISList: number = 1;
  public actionPointList: PostModel[] = [];
  public filteredActionPoints: PostModel[] = [];

  searchActionPoint = new FormControl('');

  private modalController: ModalController = inject(ModalController);
  private userService: UserService = inject(UserService);
  private auth: AuthService = inject(AuthService);
  private actionPointService: ActionPointService = inject(ActionPointService);
  public globalService: GlobalService = inject(GlobalService);
  public translate: TranslateService = inject(TranslateService);

  ngOnInit() {
    this.loadInitialPage()
    this.setupSearch();
  }

  ionViewWillEnter() {
    if (this.actionPointService.selectedActionPointId) {
      this.selectedActionPoint = this.actionPointService.selectedActionPointId;
    } else {
      this.selectedActionPoint = null;
    }
    this.actionPointService.selectedActionPointId = null;
  }

  async checkIfMoreNeeded() {
    const scrollElement = await this.content.getScrollElement();

    const isScrollable = scrollElement.scrollHeight > scrollElement.clientHeight;
    if (!isScrollable && !this.infiniteScroll.disabled) {
      this.loadData({target: this.infiniteScroll});
    }
  }

  dismiss() {
    this.modalController.dismiss('', 'cancel', 'moment-select-ap-modal');
  }

  loadInitialPage() {
    this.loading = true;
    if(this.type === 'user'){
      this.userService.apiGetUserAndAdoptedActionPoints(this.auth.user.id, this.pageISList).subscribe({
        next: (result: ApiResponse) => {
          if (result.status === true) {
            const newItems = result.data.data;
            this.actionPointList = newItems;
            this.filteredActionPoints = [...this.actionPointList];
            this.pageISList++;
            this.hasMoreData = newItems && newItems.length > 0;
            this.actionPointListIsLoaded = true;
          }
          this.loading = false;
          this.checkIfMoreNeeded()
        },
        error: () => {
          this.loading = false;
        }
      });
    } else {
      this.userService.apiGetCompanyAndAdoptedActionPoints(this.id, this.pageISList).subscribe({
        next: (result: ApiResponse) => {
          if (result.status === true) {
            const newItems = result.data.data;
            this.actionPointList = newItems;
            this.filteredActionPoints = [...this.actionPointList];
            this.pageISList++;
            this.hasMoreData = newItems && newItems.length > 0;
            this.actionPointListIsLoaded = true;
          }
          this.loading = false;
          this.checkIfMoreNeeded()
        },
        error: () => {
          this.loading = false;
        }
      });
    }

  }

  async loadAllActionPoints(): Promise<void> {
    this.pageISList = 1;
    this.hasMoreData = true;
    this.actionPointList = [];

    while (this.hasMoreData) {
      try {
        let result: ApiResponse | undefined;

        if(this.type === 'user'){
          result = await lastValueFrom(
            this.userService.apiGetUserAndAdoptedActionPoints(this.auth.user.id, this.pageISList)
          );
        }else {
          result = await lastValueFrom(
            this.userService.apiGetCompanyAndAdoptedActionPoints(this.id, this.pageISList)
          );
        }


        if (result && result.status === true) {
          const newItems = result.data.data;
          this.actionPointList.push(...newItems);
          this.pageISList++;

          if (!newItems || newItems.length === 0) {
            this.hasMoreData = false;
          }
        } else {
          this.hasMoreData = false;
        }
      } catch (err) {
        console.error('Fehler beim Laden der Action Points:', err);
        this.hasMoreData = false;
      }
    }

    this.filteredActionPoints = [...this.actionPointList];
    this.actionPointListIsLoaded = true;
  }

  loadData(event: any) {
    if (this.searchStarted) {
      event.target.complete();
      return;
    }

    if (!this.hasMoreData) {
      event.target.complete();
      return;
    }

    this.loading = true;
    if(this.type === 'user'){
      this.userService.apiGetUserAndAdoptedActionPoints(this.auth.user.id, this.pageISList).subscribe({
        next: (result: ApiResponse) => {
          if (result.status === true) {
            const newItems = result.data.data;
            this.actionPointList.push(...newItems);
            this.filteredActionPoints = [...this.actionPointList];
            this.pageISList++;
            this.loading = false;
            if (!newItems || newItems.length === 0) {
              this.hasMoreData = false;
            }
          }
          this.loading = false;
          event.target.complete();
        },
        error: () => {
          this.loading = false;
          event.target.complete();
        }
      });
    } else {
      this.userService.apiGetCompanyAndAdoptedActionPoints(this.id, this.pageISList).subscribe({
        next: (result: ApiResponse) => {
          if (result.status === true) {
            const newItems = result.data.data;
            this.actionPointList.push(...newItems);
            this.filteredActionPoints = [...this.actionPointList];
            this.pageISList++;
            this.loading = false;
            if (!newItems || newItems.length === 0) {
              this.hasMoreData = false;
            }
          }
          this.loading = false;
          event.target.complete();
        },
        error: () => {
          this.loading = false;
          event.target.complete();
        }
      });
    }

  }

  virtualScrolled(event: any) {
    event.stopPropagation();
  }


  public async selectActionPoint(actionPointId: number) {
    this.selectedActionPoint = actionPointId;
    this.actionPointService.selectedActionPointId = actionPointId;

    const modal = await this.modalController.getTop();
    if (modal) {
      modal.dispatchEvent(new CustomEvent('apSelected', { detail: actionPointId }));
      await this.globalService.presentToast(this.translate.instant('txt_action_point_attached'));
      await modal.setCurrentBreakpoint(0.1);
    }
    if(this.globalService.isWeb || this.momentCard){
      await this.modalController.dismiss(actionPointId, 'submit', 'moment-select-ap-modal');
    }
  }


  doRefresh(event: any) {
    event.target.complete();
  }

  setupSearch() {
    this.searchActionPoint.valueChanges
      .pipe(debounceTime(300))
      .subscribe(async searchText => {
        const query = (searchText || '').toLowerCase();

        if (query.length === 0) {
          this.filteredActionPoints = [...this.actionPointList];
          this.searchStarted = false;
          return;
        }

        if (!this.searchStarted) {
          this.searchStarted = true;
          this.loading = true;

          await this.loadAllActionPoints();

          this.loading = false;
        }

        this.filteredActionPoints = this.actionPointList.filter(ap =>
          ap.title?.toLowerCase().includes(query)
        );
      });
  }
}
